import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DashboardLogo } from './DashboardLogo';
import { DashboardTabs } from './DashboardTabs';
import { Badge } from '@/components/ui/badge';
import { CalendarRange, LogOut, User, Bell, Settings, BarChart3, Shield, Activity } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import catalogTasks from '@/data/catalog_tasks.json';
import incidents from '@/data/incidents.json';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';

interface DashboardLayoutProps {
  isLoading: boolean;
  filters: {
    selectedApi: string;
    setSelectedApi: (value: string) => void;
    timeRange: string;
    setTimeRange: (value: string) => void;
    ticketType: string;
    setTicketType: (value: string) => void;
    priority: string;
    setPriority: (value: string) => void;
    assignmentGroup: string;
    setAssignmentGroup: (value: string) => void;
    dateRange: DateRange | undefined;
    setDateRange: (value: DateRange | undefined) => void;
  };
  apiNames: string[];
  assignmentGroups: string[];
  children: React.ReactNode;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

export function DashboardLayout({
  isLoading,
  filters,
  apiNames,
  assignmentGroups,
  children,
  activeTab = "overview",
  onTabChange = () => {}
}: DashboardLayoutProps) {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  const [slaSettings, setSlaSettings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('slaSettings') || '') || {
        incidents: { critical: 1, high: 2, medium: 3, normal: 7 },
        tasks: { critical: 2, high: 3, medium: 4, normal: 5 }
      };
    } catch {
      return {
        incidents: { critical: 1, high: 2, medium: 3, normal: 7 },
        tasks: { critical: 2, high: 3, medium: 4, normal: 5 }
      };
    }
  });
  const [preferences, setPreferences] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('notificationPreferences') || '') || {
        dueDateAlerts: true,
        slaAlerts: true,
        criticalAlerts: true,
        warningAlerts: true
      };
    } catch {
      return {
        dueDateAlerts: true,
        slaAlerts: true,
        criticalAlerts: true,
        warningAlerts: true
      };
    }
  });

  // Notification logic using imported JSON data and preferences
  const [notifications, setNotifications] = useState<{
    message: string,
    severity: 'critical' | 'warning',
    shortDescription?: string,
    openedDate?: string
  }[]>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  useEffect(() => {
    localStorage.setItem('slaSettings', JSON.stringify(slaSettings));
    localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
  }, [slaSettings, preferences]);

  useEffect(() => {
    const now = new Date();
    const alerts: {
      message: string,
      severity: 'critical' | 'warning',
      shortDescription?: string,
      openedDate?: string
    }[] = [];
    // Incidents: SLA logic
    incidents.forEach((incident: any) => {
      if (!incident.Opened || incident.Closed) return;
      const openedDate = new Date(incident.Opened);
      const daysOpen = Math.floor((now.getTime() - openedDate.getTime()) / (1000 * 60 * 60 * 24));
      const priority = incident.Priority?.toLowerCase() || '';
      let slaDays = slaSettings.incidents.normal;
      if (priority.includes('critical') || priority === '1') slaDays = slaSettings.incidents.critical;
      else if (priority.includes('high') || priority === '2') slaDays = slaSettings.incidents.high;
      else if (priority.includes('medium') || priority === '3') slaDays = slaSettings.incidents.medium;
      if (preferences.slaAlerts && daysOpen >= slaDays - 1) {
        const severity = daysOpen >= slaDays ? 'critical' : 'warning';
        if ((severity === 'critical' && preferences.criticalAlerts) || (severity === 'warning' && preferences.warningAlerts)) {
          alerts.push({
            message: `Incident ${incident.Number} (${incident.Priority}) is ${daysOpen >= slaDays ? 'breaching' : 'approaching'} SLA`,
            severity,
            shortDescription: incident['Short description'] || incident['short_description'] || 'No summary',
            openedDate: incident.Opened
          });
        }
      }
    });
    // Catalog Tasks: Due date logic
    catalogTasks.forEach((task: any) => {
      if (!task.Opened || !task["Due date"] || task.State === 'Closed Complete') return;
      const dueDate = new Date(task["Due date"]);
      const daysUntilDue = Math.floor((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      // Due today notification
      if (preferences.dueDateAlerts && daysUntilDue === 0) {
        if (preferences.criticalAlerts) {
          alerts.push({
            message: `Task ${task.Number} is due today (Assigned to: ${task["Assigned to"] || 'Unassigned'})`,
            severity: 'critical',
            shortDescription: task['Short description'] || task['short_description'] || 'No summary',
            openedDate: task.Opened
          });
        }
      }
      // Overdue or due soon
      if (preferences.dueDateAlerts && daysUntilDue < 0) {
        if (preferences.criticalAlerts) {
          alerts.push({
            message: `Task ${task.Number} is overdue by ${Math.abs(daysUntilDue)} days (Assigned to: ${task["Assigned to"] || 'Unassigned'})`,
            severity: 'critical',
            shortDescription: task['Short description'] || task['short_description'] || 'No summary',
            openedDate: task.Opened
          });
        }
      } else if (preferences.dueDateAlerts && daysUntilDue > 0 && daysUntilDue <= 2) {
        if (preferences.warningAlerts) {
          alerts.push({
            message: `Task ${task.Number} is due in ${daysUntilDue} days (Assigned to: ${task["Assigned to"] || 'Unassigned'})`,
            severity: 'warning',
            shortDescription: task['Short description'] || task['short_description'] || 'No summary',
            openedDate: task.Opened
          });
        }
      }
    });
    setNotifications(alerts);
    setTotalAlerts(alerts.length);
  }, [slaSettings, preferences]);

  const handleLogout = () => {
    localStorage.removeItem("itsm-auth-token");
    localStorage.removeItem("itsm-user");
    localStorage.removeItem("itsm-user-name");
    localStorage.removeItem("itsm-user-role");
    toast.info("You've been logged out");
    navigate("/login");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="animate-pulse space-y-4 flex flex-col items-center">
          <div className="h-10 w-48 bg-gray-200 rounded-md"></div>
          <div className="h-4 w-64 bg-gray-200 rounded-md"></div>
          <div className="h-4 w-52 bg-gray-200 rounded-md"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto py-4 px-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <DashboardLogo className="h-10" />
              <div className="hidden md:block">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  ITSM Executive Dashboard
                </h1>
                <p className="text-sm text-gray-500 mt-1">Enterprise-wide Service Management Insights</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="relative rounded-full hover:bg-gray-100 p-2">
                      <Bell size={18} className="text-gray-600" />
                      {totalAlerts > 0 && (
                        <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                          {totalAlerts}
                        </Badge>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 max-h-[350px] overflow-y-auto">
                    <div className="p-2">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold">Recent Notifications</h3>
                        <Button size="sm" variant="ghost" onClick={() => setShowSettings((v) => !v)}>
                          <Settings size={16} />
                        </Button>
                      </div>
                      {showSettings ? (
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-xs font-semibold mb-1">Notification Preferences</h4>
                            <div className="flex items-center justify-between mb-1">
                              <span>Due Date Alerts</span>
                              <Switch checked={preferences.dueDateAlerts} onCheckedChange={() => setPreferences(p => ({...p, dueDateAlerts: !p.dueDateAlerts}))} />
                            </div>
                            <div className="flex items-center justify-between mb-1">
                              <span>SLA Alerts</span>
                              <Switch checked={preferences.slaAlerts} onCheckedChange={() => setPreferences(p => ({...p, slaAlerts: !p.slaAlerts}))} />
                            </div>
                            <div className="flex items-center justify-between mb-1">
                              <span>Critical Alerts</span>
                              <Switch checked={preferences.criticalAlerts} onCheckedChange={() => setPreferences(p => ({...p, criticalAlerts: !p.criticalAlerts}))} />
                            </div>
                            <div className="flex items-center justify-between mb-1">
                              <span>Warning Alerts</span>
                              <Switch checked={preferences.warningAlerts} onCheckedChange={() => setPreferences(p => ({...p, warningAlerts: !p.warningAlerts}))} />
                            </div>
                          </div>
                          <div>
                            <h4 className="text-xs font-semibold mb-1 mt-2">SLA Thresholds (days)</h4>
                            <div className="mb-1 text-xs font-semibold">Incidents</div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              {(['critical','high','medium','normal'] as const).map(priority => (
                                <div key={priority} className="flex items-center gap-1">
                                  <span className="capitalize w-12">{priority}</span>
                                  <Input type="number" min={1} className="w-14 h-7 px-1 py-0 text-xs" value={slaSettings.incidents[priority]} onChange={e => setSlaSettings(s => ({...s, incidents: {...s.incidents, [priority]: parseInt(e.target.value) || 1}}))} />
                                </div>
                              ))}
                            </div>
                            <div className="mb-1 text-xs font-semibold">Tasks</div>
                            <div className="grid grid-cols-2 gap-2">
                              {(['critical','high','medium','normal'] as const).map(priority => (
                                <div key={priority} className="flex items-center gap-1">
                                  <span className="capitalize w-12">{priority}</span>
                                  <Input type="number" min={1} className="w-14 h-7 px-1 py-0 text-xs" value={slaSettings.tasks[priority]} onChange={e => setSlaSettings(s => ({...s, tasks: {...s.tasks, [priority]: parseInt(e.target.value) || 1}}))} />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        totalAlerts === 0 ? (
                          <p className="text-sm text-muted-foreground">No active notifications</p>
                        ) : (
                          <div className="space-y-2">
                            {notifications.map((alert, i) => (
                              <div
                                key={i}
                                className={`p-2 rounded-md text-sm cursor-pointer ${
                                  alert.severity === 'critical' ? 'bg-destructive/10' : 'bg-warning/10'
                                }`}
                                onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                              >
                                {alert.message}
                                {expandedIdx === i && (
                                  <div className="mt-2 p-2 bg-white border rounded text-xs text-gray-700 shadow">
                                    <div><span className="font-semibold">Summary:</span> {alert.shortDescription}</div>
                                    <div><span className="font-semibold">Opened:</span> {alert.openedDate ? format(new Date(alert.openedDate), 'PPP p') : 'N/A'}</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="ghost" size="icon" className="rounded-full hover:bg-gray-100">
                  <Settings size={18} className="text-gray-600" />
                </Button>
              </div>
              
              <Badge variant="outline" className="hidden md:inline-flex bg-blue-50 text-blue-600 border-blue-200">
                itsm@cyntralabs.com
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full hover:bg-gray-100">
                    <User size={18} className="text-gray-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">{localStorage.getItem("itsm-user-name") || localStorage.getItem("itsm-user") || "User"}</p>
                    <p className="text-xs text-gray-500">{localStorage.getItem("itsm-user-role") || "Executive Access"}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto p-6 pb-16">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Executive Overview</h2>
            </div>
            <div className="h-6 w-px bg-gray-200"></div>
            <div className="space-x-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                <Shield className="h-3 w-3 mr-1" />
                Enterprise
              </Badge>
              <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">
                <BarChart3 className="h-3 w-3 mr-1" />
                Analytics
              </Badge>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <CalendarRange className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">From:</span>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal w-[140px] bg-white shadow-sm",
                      "hover:bg-gray-50 transition-colors duration-200"
                    )}
                  >
                    {filters.dateRange?.from ? (
                      format(filters.dateRange.from, "LLL dd, y")
                    ) : (
                      "Select date"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 min-w-[320px]" align="start">
                  <Calendar
                    initialFocus
                    mode="single"
                    selected={filters.dateRange?.from}
                    onSelect={(date) => {
                      filters.setDateRange({
                        from: date,
                        to: filters.dateRange?.to
                      });
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">To:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal w-[140px] bg-white shadow-sm",
                      "hover:bg-gray-50 transition-colors duration-200"
                    )}
                  >
                    {filters.dateRange?.to ? (
                      format(filters.dateRange.to, "LLL dd, y")
                    ) : (
                      "Select date"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 min-w-[320px]" align="start">
                  <Calendar
                    initialFocus
                    mode="single"
                    selected={filters.dateRange?.to}
                    onSelect={(date) => {
                      filters.setDateRange({
                        from: filters.dateRange?.from,
                        to: date
                      });
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        

        
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <DashboardTabs activeTab={activeTab} onTabChange={onTabChange}>
            {children}
          </DashboardTabs>
        </div>
      </main>
    </div>
  );
}
